/**
 * 설치 장소 페이지 (DESIGN §3.8).
 *
 * **묻는 것이 다르다.** 다른 페이지는 "잘 도는가"(가동률)를 묻지만 여기는
 * "어디에 놓였고 어떻게 쓰이나"(수요·형상)를 묻는다. 실측이 이 축의 존재
 * 이유를 증명한다 — 1회 충전 소요가 휴게소 25분 vs 공동주택 324분이다.
 *
 * 개요에 4번째 섹션으로 붙이지 않은 이유는 OverviewPage 의 불변식이다:
 * "비교 축은 페이지가 하나만 갖는다". 여기는 축이 장소로 고정이고 형태가
 * 그래프가 아니라 다열 표다.
 *
 * **시간 세 가지를 섞지 않는다** (§3.8.3). 충전 ≠ 점유 ≠ 잔류이며 그 차이
 * 자체가 장소의 성격이다.
 */

import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { Section } from '@/components/Section'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { int, pct } from '@/lib/fmt'
import type { Envelope } from '@/lib/types'
import { toQueryFilters, useFilters } from '@/stores/filters'
import { usePlaceCharts } from './places/placeCharts'
import './PlacesPage.css'

type Axis = 'group' | 'kind'

const EMPTY =
  '이 빌드에는 장소 프로파일이 없습니다 — 오른쪽 위 「지금 빌드」를 누르면 만들어집니다. 필터와는 무관합니다.'

interface PlaceRow {
  axis: string
  code: string
  n_chargers: number
  n_stations: number
  per_station_p50: number | null
  per_station_p90: number | null
  per_station_max: number | null
  n_large_stations: number | null
  n_fast: number | null
  n_slow: number | null
  n_output_unknown: number | null
  output_p50: number | null
  limit_share: number | null
  charge_min_p50: number | null
  charge_min_p90: number | null
  n_charge_sessions: number | null
  occupancy_min_p50: number | null
  occupancy_min_p90: number | null
  n_occupancy: number | null
  n_occupancy_censored: number | null
  tail_min_p50: number | null
  tail_min_p75: number | null
}

interface DailyPoint {
  date_kst: string
  n_chargers: number
  availability: number | null
  coverage: number | null
  utilization_min_per_charger: number | null
}

interface PlaceData {
  axis: string
  rows: PlaceRow[]
  series: Record<string, DailyPoint[]>
  /** 못 내는 것의 **사유 문자열**. 값이 아니라 사유가 온다 (§3.8.4) */
  unavailable: Record<string, string>
}

/** 재편 8군 표시명. 서버 코드와 1:1 — 여기서 분류를 다시 만들지 않는다 */
const GROUP_LABEL: Record<string, string> = {
  home: '공동주택·주거',
  work: '직장·사업장',
  bus: '버스·상용 차고지',
  parking: '주차시설',
  public: '공공기관',
  rest: '휴게·관광',
  commerce: '상업·생활·교육',
  misc: '기타·세부 미상',
  unknown: '미상 (분류 없음)',
}

const KIND_LABEL: Record<string, string> = {
  A0: '공공시설',
  B0: '주차시설',
  C0: '휴게시설',
  D0: '관광시설',
  E0: '상업시설',
  F0: '차량정비시설',
  G0: '기타시설',
  H0: '공동주택시설',
  I0: '근린생활시설',
  J0: '교육문화시설',
  unknown: '미상 (코드 없음)',
}

function label(axis: Axis, code: string): string {
  return (axis === 'group' ? GROUP_LABEL : KIND_LABEL)[code] ?? code
}

/** 분 → 사람이 읽는 시간. NULL 은 0 이 아니라 '표본 없음'이다 */
function mins(v: number | null): string {
  if (v == null) return '—'
  if (v < 90) return `${v.toFixed(0)}분`
  return `${(v / 60).toFixed(1)}시간`
}

function num(v: number | null, d = 0): string {
  return v == null ? '—' : v.toFixed(d)
}

export function PlacesPage() {
  const [axis, setAxis] = useState<Axis>('group')
  // 이 화면은 전국 고정이라 필터를 타지 않는다. 다만 API 봉투가 mode 를
  // 요구하므로(meta 에 어느 정의의 빌드인지 실어야 한다) 그대로 넘긴다.
  const filters = useFilters()

  const q = useQuery({
    queryKey: ['metrics/places', axis, filters.mode],
    queryFn: ({ signal }) =>
      fetchEnvelope<PlaceData>('/metrics/places', toQueryFilters(filters), { axis }, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<PlaceData> | undefined
  const rows = env?.data.rows ?? []
  const unavailable = env?.data.unavailable ?? {}
  const series = env?.data.series ?? {}
  const total = rows.reduce((a, r) => a + r.n_chargers, 0)
  const charts = usePlaceCharts({ rows, series, label: (c) => label(axis, c) })

  /**
   * **분류 축은 페이지가 하나만 갖는다** (DESIGN §8.6.4).
   * 카드 안에 두면 그 카드만 바꾸는 것처럼 보이고, 여러 카드에 두면 같은
   * 상태를 조작하는 곳이 여럿이 된다. 실제로 세 카드에 중복돼 있었다.
   */
  const axisControl = (
    <div className="places__axis" role="group" aria-label="장소 분류 축">
      {(
        [
          ['group', '재편 8군'],
          ['kind', '원본 코드 10종'],
        ] as const
      ).map(([k, t]) => (
        <button
          key={k}
          type="button"
          className={`places__axisbtn${axis === k ? ' is-on' : ''}`}
          aria-pressed={axis === k}
          onClick={() => setAxis(k)}
        >
          {t}
        </button>
      ))}
    </div>
  )

  return (
    <div className="places">
      <header className="places__intro">
        <p className="places__lead">
          같은 충전기라도 <strong>어디에 놓였는지</strong>에 따라 쓰이는 방식이 완전히 달라집니다.
          1회 충전 소요가 휴게소 25분, 공동주택 5.4시간입니다 — <strong>13배</strong> 차이입니다.
          이 페이지는 가동률이 아니라 <strong>수요와 형상</strong>을 봅니다.
        </p>
        <div className="places__axisrow">
          <span className="places__axislabel">분류 축</span>
          {axisControl}
          <span className="places__axishint">
            아래 <strong>모든 그래프와 표</strong>가 함께{' '}
            <strong>{axis === 'group' ? '재편 8군' : '원본 코드 10종'}</strong> 기준으로 바뀝니다
          </span>
        </div>
        <p className="places__note">
          <strong>전국 모집단 고정입니다.</strong> 왼쪽 필터를 타지 않습니다 — 부분집합을 만들면
          개소당 대수와 구성비의 분모가 무너집니다.
        </p>
      </header>

      <Section
        index={1}
        title="한눈에 보기"
        lead="구성 · 급속 비중 · 이용 추이. 세 그래프에서 같은 장소는 같은 색이고, 진할수록 규모가 큽니다."
        layout="split"
      >
        <ChartFrame
          title="충전기 구성"
          question={`${axis === 'group' ? '재편 8군' : '원본 코드'}별 충전기 비중`}
          meta={env?.meta}
          isLoading={q.isLoading}
          error={q.error as Error | null}
          isEmpty={!q.isLoading && rows.length === 0}
          emptyText={EMPTY}
          onRetry={() => void q.refetch()}
          encoding={[
            { role: '조각 크기', what: '충전기 수', detail: '가운데 숫자가 총계입니다 — 조각 면적 비교의 약점을 숫자로 보완합니다.' },
            { role: '색', what: '규모 순 단일 색상', detail: '이 프로젝트 팔레트에는 카테고리 색이 둘뿐이라 9개 장소에 색을 지어내지 않았습니다. 진할수록 큽니다.' },
          ]}
          reading="공동주택이 전체의 70%를 차지합니다. 나머지 여덟 범주를 다 합쳐도 그 절반이 안 됩니다 — 국내 충전 인프라의 형상 그 자체입니다."
        >
          <EChart option={charts.donut} ariaLabel="장소별 충전기 구성비 도넛" height={300} />
        </ChartFrame>

        <ChartFrame
          title="급속 비중"
          question="장소마다 급속(50kW 이상)이 얼마나 되나"
          meta={env?.meta}
          isLoading={q.isLoading}
          error={q.error as Error | null}
          isEmpty={!q.isLoading && rows.length === 0}
          emptyText={EMPTY}
          onRetry={() => void q.refetch()}
          encoding={[
            { role: '가로축', what: '급속 비율 (0~100%)', detail: '분모는 정격이 확인된 충전기입니다 — 정격 미상을 완속으로 세면 비율이 낮게 나옵니다.' },
            { role: '세로축', what: '장소 · 급속 비율 오름차순' },
          ]}
          reading="버스 차고지 100%, 휴게·관광 67%, 공동주택 0.9%. 머무는 시간이 짧은 곳일수록 급속입니다 — 설치장소가 곧 충전 방식을 정합니다."
        >
          <EChart option={charts.bar} ariaLabel="장소별 급속 비율 막대" height={300} />
        </ChartFrame>
      </Section>

      <Section
        index={2}
        title="날마다 얼마나 쓰이나"
        lead="충전기 1대가 하루 몇 분을 충전 상태로 보냈는지입니다. kWh 가 없으므로 이것이 수요의 대리 지표입니다."
        layout="full"
      >
        <ChartFrame
          title="장소별 일별 이용 추이"
          question="1대당 하루 충전 분 — 장소마다 수요의 크기와 리듬이 다르다"
          meta={env?.meta}
          isLoading={q.isLoading}
          error={q.error as Error | null}
          isEmpty={!q.isLoading && Object.keys(series).length === 0}
          emptyText={EMPTY}
          onRetry={() => void q.refetch()}
          encoding={[
            { role: '가로축', what: '날짜 (KST)', detail: '수집이 시작·종료된 날은 하루가 온전하지 않아 양 끝이 낮게 나옵니다.' },
            { role: '세로축', what: '충전기 1대당 하루 충전 분', detail: '그 장소의 총 충전 시간을 충전기 수로 나눈 값입니다. 한 번도 안 쓰인 충전기도 분모에 들어갑니다.' },
            { role: '색', what: '위 그래프와 같은 규모 순 색' },
            { role: '끊긴 선', what: '그날 관측이 없었다', detail: '결측을 0 으로 이어 붙이지 않습니다 — 안 쓴 것과 모르는 것은 다릅니다.' },
          ]}
          reading="선이 위에 있을수록 그 장소의 충전기가 바쁩니다. 공동주택은 대수가 압도적이지만 1대당으로 보면 다른 장소와 견줄 만합니다 — 총량과 밀도는 다른 이야기입니다."
          notes={[
            {
              label: '요일 비교는 하지 않는다.',
              text: '관측 창이 짧아 요일이 고르게 들어 있지 않습니다. 지금 창에서 요일별 차이를 읽으면 이용 패턴이 아니라 창 구성을 읽는 것입니다.',
            },
          ]}
        >
          <EChart option={charts.line} ariaLabel="장소별 일별 1대당 충전 분 선그래프" height={340} />
        </ChartFrame>
      </Section>

      <Section index={3} title="어디에 얼마나 놓였나" lead="분류 축을 바꿔 원본 코드와 재편 분류를 견줄 수 있습니다." layout="full">
        <ChartFrame
          title="설치장소별 규모와 형상"
          question="충전기가 어디에 몰려 있고, 한 곳에 몇 대씩 놓이나"
          meta={env?.meta}
          isLoading={q.isLoading}
          error={q.error as Error | null}
          isEmpty={!q.isLoading && rows.length === 0}
          emptyText={EMPTY}
          onRetry={() => void q.refetch()}
          encoding={[
            { role: '행', what: axis === 'group' ? '재편 장소군 8종 + 미상' : '원본 kind 코드 10종 + 미상' },
            {
              role: '개소당 대수',
              what: '중앙값 · p90 · 최대',
              detail: '한 충전소(stat_id)에 몇 대가 놓이는지입니다. 개소가 물리적 주차장 하나와 항상 같지는 않습니다 — 대단지 아파트는 한 주차장에 stat_id 가 여럿일 수 있습니다.',
            },
            {
              role: '급속',
              what: '정격 50kW 이상 비율',
              detail: '급속·완속·판정불가 3분류입니다. 정격이 없는 충전기를 완속으로 세면 비율이 낮게 나오므로 따로 셉니다.',
            },
          ]}
          reading={
            axis === 'group'
              ? '원본 코드표가 「공동주택시설」 안에 사업장(사옥)을 넣어 두어 공동주택과 직장을 나눌 수 없었습니다. 재편 8군은 그것을 분리하고, 7개 코드에 흩어져 있던 버스 차고지를 커넥터 규격으로 따로 뽑았습니다.'
              : '공공데이터 코드표 원본 그대로입니다. 「공동주택시설」에 사업장(사옥) 13,623기가 섞여 있고 2위가 「기타시설」인 것이 원본의 상태입니다.'
          }
          notes={[
            {
              label: '원본 코드를 지우지 않았다.',
              text: 'kind 오타(알파벳 O)는 같은 행의 상세코드로 재유도해 바로잡았지만, 원본 값은 kind_raw 로 보존해 되짚을 수 있게 두었습니다.',
            },
            {
              label: '「미상」을 채우지 않는다.',
              text: '분류 코드가 비어 있는 110기는 이름으로 보면 아파트·주민센터·마트가 뒤섞여 하나로 뭉뚱그릴 수 없습니다. 추정해 채우지 않고 미상으로 남깁니다.',
            },
            {
              label: '물류센터는 분류할 수 없다.',
              text: unavailable.logistics_segment ?? '',
            },
          ]}
        >
          <div className="ctable">
            <table className="data-table">
              <caption className="sr-only">설치장소별 충전기·개소 규모</caption>
              <thead>
                <tr>
                  <th scope="col">장소</th>
                  <th scope="col" className="ta-r">충전기</th>
                  <th scope="col" className="ta-r">비중</th>
                  <th scope="col" className="ta-r">개소</th>
                  <th scope="col" className="ta-r">개소당 (중앙)</th>
                  <th scope="col" className="ta-r">p90</th>
                  <th scope="col" className="ta-r">최대</th>
                  <th scope="col" className="ta-r">20기+ 개소</th>
                  <th scope="col" className="ta-r">급속</th>
                  <th scope="col" className="ta-r">정격(중앙)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const known = (r.n_fast ?? 0) + (r.n_slow ?? 0)
                  return (
                    <tr key={r.code} className={r.code === 'unknown' ? 'is-na' : undefined}>
                      <th scope="row">{label(axis, r.code)}</th>
                      <td className="ta-r num">{int(r.n_chargers)}</td>
                      <td className="ta-r num">{total ? pct(r.n_chargers / total, 1) : '—'}</td>
                      <td className="ta-r num">{int(r.n_stations)}</td>
                      <td className="ta-r num">{num(r.per_station_p50)}</td>
                      <td className="ta-r num">{num(r.per_station_p90)}</td>
                      <td className="ta-r num">{num(r.per_station_max)}</td>
                      <td className="ta-r num">{int(r.n_large_stations ?? 0)}</td>
                      <td className="ta-r num">
                        {known ? pct((r.n_fast ?? 0) / known, 1) : '판정 불가'}
                      </td>
                      <td className="ta-r num">{r.output_p50 == null ? '—' : `${num(r.output_p50)}kW`}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </ChartFrame>
      </Section>

      <Section
        index={4}
        title="얼마나 오래 쓰이나"
        lead="충전 시간 · 커넥터 점유 시간 · 충전 후 잔류 시간은 서로 다른 것입니다. 그 차이가 장소의 성격입니다."
        layout="full"
      >
        <ChartFrame
          title="장소별 이용 시간"
          question="충전에 걸린 시간과, 그 뒤로 커넥터를 붙잡고 있는 시간"
          meta={env?.meta}
          isLoading={q.isLoading}
          error={q.error as Error | null}
          isEmpty={!q.isLoading && rows.length === 0}
          emptyText={EMPTY}
          onRetry={() => void q.refetch()}
          encoding={[
            {
              role: '충전 시간',
              what: 'API 가 보고한 충전 소요 (중앙값)',
              detail: '사업자가 기록한 충전 시작~종료입니다. 이것이 "얼마나 충전했나"에 가장 가까운 값입니다.',
            },
            {
              role: '커넥터 점유',
              what: '상태가 「충전중」인 구간 지속 (중앙값)',
              detail: '충전이 끝나도 사업자가 상태를 내리지 않으면 계속 충전중으로 보고됩니다. 그래서 충전 시간보다 깁니다.',
            },
            {
              role: '충전 후 잔류',
              what: '점유 종료 − 충전 종료 (p75)',
              detail: '만충 뒤에도 커넥터가 물려 있던 시간입니다. 절반은 2분 안에 끝나고 나머지가 몇 시간씩 남는 이봉 분포라 중앙값이 아니라 p75 로 봅니다.',
            },
          ]}
          reading="공동주택은 충전 5.4시간인데 점유가 9.3시간입니다 — 밤새 꽂아 둡니다. 버스 차고지는 충전 27분에 잔류가 0분입니다 — 끝나면 바로 뺍니다. 같은 '이용'이라도 성격이 정반대입니다."
          notes={[
            {
              label: '이것은 주차 체류 시간이 아니다.',
              text: unavailable.parking_dwell_min ?? '',
            },
            {
              label: '1대에 필요 충전량은 낼 수 없다.',
              text: unavailable.charge_kwh ?? '',
            },
            {
              label: '점유 구간은 24시간에서 잘린다.',
              text: 'STALE 임계를 넘으면 상태를 믿을 수 없다고 보고 구간을 끊습니다. 그래서 공동주택처럼 긴 쪽의 분포 상단은 원리적으로 볼 수 없습니다. 잘린 건수를 아래 표에 그대로 싣습니다.',
            },
          ]}
        >
          <div className="ctable">
            <table className="data-table">
              <caption className="sr-only">설치장소별 충전·점유·잔류 시간</caption>
              <thead>
                <tr>
                  <th scope="col">장소</th>
                  <th scope="col" className="ta-r">충전 (중앙)</th>
                  <th scope="col" className="ta-r">충전 p90</th>
                  <th scope="col" className="ta-r">점유 (중앙)</th>
                  <th scope="col" className="ta-r">잔류 p75</th>
                  <th scope="col" className="ta-r">이용제한</th>
                  <th scope="col" className="ta-r">세션 표본</th>
                  <th scope="col" className="ta-r">점유 표본 (잘림)</th>
                  <th scope="col" className="places__gap">1대당 충전량</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.code} className={r.code === 'unknown' ? 'is-na' : undefined}>
                    <th scope="row">{label(axis, r.code)}</th>
                    <td className="ta-r num">{mins(r.charge_min_p50)}</td>
                    <td className="ta-r num">{mins(r.charge_min_p90)}</td>
                    <td className="ta-r num">{mins(r.occupancy_min_p50)}</td>
                    <td className="ta-r num">{mins(r.tail_min_p75)}</td>
                    <td className="ta-r num">
                      {r.limit_share == null ? '—' : pct(r.limit_share, 1)}
                    </td>
                    <td className="ta-r num">{int(r.n_charge_sessions ?? 0)}</td>
                    <td className="ta-r num">
                      {int(r.n_occupancy ?? 0)}
                      {(r.n_occupancy_censored ?? 0) > 0 && (
                        <span className="places__censor"> ({int(r.n_occupancy_censored ?? 0)})</span>
                      )}
                    </td>
                    {/* 값이 아니라 **사유**가 들어가는 칸이다 (§3.8.4).
                        열을 없애면 "요청한 게 왜 빠졌지"가 되고, 값을 만들면 거짓이 된다 */}
                    <td className="places__gap hatch-na">
                      <span className="places__gaptext">수집 불가</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartFrame>
      </Section>
    </div>
  )
}
